import { HttpErrorResponse } from '@angular/common/http';
import { createAction, props } from '@ngrx/store';
import {
    AlertInterval,
    ClearDeviceCareParameters,
    DeviceCare,
    DeviceCareInstallation,
    DeviceCarePutRequest,
    Metric,
    MetricOverridesPutRequest,
    MetricPutRequest,
    MetricsStatus,
    Report,
    SmtpStatus,
} from '@myrtls/api-interfaces';
import { DateRange } from '../../shared/models/date-range.enum';
import { MetricDataFetchType } from '../../shared/models/metric-data-fetch-type.enum';
import {
    MetricDashboardDataFragment,
    MetricDataFragment,
} from '../../shared/models/metric-data-fragment.model';
import { DeviceCarePeriod } from '../../shared/models/device-care-period.model';
import { DeviceCareInfo } from './monitoring.reducer';

export const getSiteMetricsConfigRequestAction = createAction(
    '[Monitoring] Get Site Metrics Config Request',
    props<{ dataFetch: MetricDataFetchType }>(),
);

export const getSiteMetricsConfigSuccessAction = createAction(
    '[Monitoring] Get Site Metrics Config Success',
    props<{
        siteId: string;
        metrics: Metric[];
        dataFetch: MetricDataFetchType;
    }>(),
);

export const getSiteMetricsConfigFailureAction = createAction(
    '[Monitoring] Get Site Metrics Config Failure',
    props<{ error: HttpErrorResponse }>(),
);

export const getSiteMetricDataRequestAction = createAction(
    '[Monitoring] Get Site Metric Data Request',
    props<{
        siteId: string;
        metricId: string;
        query: string;
        deviceCare: DeviceCareInfo;
        overrides: string[];
    }>(),
);

export const getSiteMetricsDataRequestAction = createAction(
    '[Monitoring] Get Site Metrics Data Request',
    props<{ siteId: string; metrics: Metric[] }>(),
);

export const getSiteMetricDataSuccessAction = createAction(
    '[Monitoring] Get Site Metric Data Success',
    props<{
        metricId: string;
        siteId: string;
        data: MetricDataFragment[];
        range: DateRange;
        from: Date;
        to: Date;
    }>(),
);

export const getSiteMetricDataFailureAction = createAction(
    '[Monitoring] Get Site Metric Data Failure',
    props<{ error: HttpErrorResponse }>(),
);

export const getSiteMetricDashboardDataRequestAction = createAction(
    '[Monitoring] Get Site Metric Dashboard Data Request',
    props<{
        siteId: string;
        metricId: string;
        query: string;
        deviceCare: DeviceCareInfo;
        overrides: string[];
        threshold?: number;
    }>(),
);

export const getSiteMetricDashboardDataSuccessAction = createAction(
    '[Monitoring] Get Site Metric Dashboard Data Success',
    props<{
        metricId: string;
        siteId: string;
        data: MetricDashboardDataFragment[];
    }>(),
);

export const getSiteMetricDashboardDataFailureAction = createAction(
    '[Monitoring] Get Site Metric Dashboard Data Failure',
    props<{ error: HttpErrorResponse }>(),
);

export const getSiteMetricFilteringDataRequestAction = createAction(
    '[Monitoring] Get Site Metric Filtering Data Request',
    props<{
        metricId: string;
    }>(),
);

export const getSiteMetricFilteringDataSuccessAction = createAction(
    '[Monitoring] Get Site Metric Filtering Data Success',
    props<{
        metricId: string;
        siteId: string;
        data: { [key: string]: string }[];
    }>(),
);

export const getSiteMetricFilteringDataFailureAction = createAction(
    '[Monitoring] Get Site Metric Filtering Data Failure',
    props<{ error: HttpErrorResponse }>(),
);

export const getSiteMetricsStatusSuccessAction = createAction(
    '[Monitoring] Get Site Metrics Status Success',
    props<{ status: MetricsStatus }>(),
);

export const getSiteMetricsStatusFailureAction = createAction(
    '[Monitoring] Get Site Metrics Status Failure',
    props<{ error: HttpErrorResponse }>(),
);

export const startPoolingSiteMetricsStatusAction = createAction(
    '[Monitoring] Start Pooling Site Metrics Status',
    props<{ intervalTime: number }>(),
);

export const stopPoolingSiteMetricsStatusAction = createAction(
    '[Monitoring] Stop Pooling Site Metrics Status',
);

export const updateSiteMetricSettingsRequestAction = createAction(
    '[Monitoring] Update Metric Settings Request',
    props<{ metricPutRequest: MetricPutRequest[] }>(),
);

export const updateSiteMetricSettingsSuccessAction = createAction(
    '[Monitoring] Update Metric Settings Success',
    props<{ siteId: string; metrics: Metric[] }>(),
);

export const updateSiteMetricSettingsFailureAction = createAction(
    '[Monitoring] Update Metric Settings Failure',
    props<{ error: HttpErrorResponse }>(),
);

export const updateSiteMetricOverridesRequestAction = createAction(
    '[Monitoring] Update Metric Overrides Request',
    props<{
        metricId: string;
        metricOverridesPutRequest: MetricOverridesPutRequest;
    }>(),
);

export const updateSiteMetricOverridesSuccessAction = createAction(
    '[Monitoring] Update Metric Overrides Success',
);

export const updateSiteMetricOverridesFailureAction = createAction(
    '[Monitoring] Update Metric Overrides Failure',
    props<{ error: HttpErrorResponse }>(),
);

export const setMetricLoadingAction = createAction(
    '[Monitoring] Set Metric Loading',
    props<{ siteId: string; metricId: string; loading: boolean }>(),
);

export const exportCSVChartDataAction = createAction(
    '[Monitoring] Export CSV Chart Data',
    props<{ metricId: string }>(),
);

export const setAlertsOnly = createAction(
    '[Monitoring] Set Alerts Only',
    props<{ alertsOnly: boolean }>(),
);

export const setIsReport = createAction(
    '[Monitoring] Set Is Report',
    props<{ isReport: boolean }>(),
);

export const selectPeriodDateRangeAction = createAction(
    '[Monitoring] Select Period Date Range',
    props<{
        siteId: string;
        dateRange: DateRange;
        from: Date;
        to: Date;
    }>(),
);

export const getSiteDeviceCareConfigRequestAction = createAction(
    '[Monitoring] Get Site Device Care Request',
    props<{
        focus?: string;
        period?: DeviceCarePeriod;
    }>(),
);

export const getSiteDeviceCareConfigSuccessAction = createAction(
    '[Monitoring] Get Site Device Care Success',
    props<{
        siteId: string;
        config: DeviceCare;
        period?: DeviceCarePeriod;
    }>(),
);

export const getSiteDeviceCareConfigFailureAction = createAction(
    '[Monitoring] Get Site Device Care Failure',
    props<{ error: HttpErrorResponse }>(),
);

export const getSiteDeviceCareInstallationRequestAction = createAction(
    '[Monitoring] Get Site Device Care Installation Request',
);

export const getSiteDeviceCareInstallationSuccessAction = createAction(
    '[Monitoring] Get Site Device Care Installation Success',
    props<{
        installation: DeviceCareInstallation;
    }>(),
);

export const getSiteDeviceCareInstallationFailureAction = createAction(
    '[Monitoring] Get Site Device Care Installation Failure',
    props<{ error: HttpErrorResponse }>(),
);

export const activateDeviceCareRequestAction = createAction(
    '[Monitoring] Activate Device Care Request',
);

export const activateDeviceCareSuccessAction = createAction(
    '[Monitoring] Activate Device Care Success',
    props<{ date: number; site: string }>(),
);

export const activateDeviceCareFailureAction = createAction(
    '[Monitoring] Activate Device Care Failure',
    props<{ error: HttpErrorResponse }>(),
);

export const updateSiteReportSettingsRequestAction = createAction(
    '[Monitoring] Update Report Settings Request',
    props<{ deviceCarePutRequest: DeviceCarePutRequest }>(),
);

export const updateSiteReportSettingsSuccessAction = createAction(
    '[Monitoring] Update Report Settings Success',
    props<{ siteId: string; config: DeviceCare }>(),
);

export const updateSiteReportSettingsFailureAction = createAction(
    '[Monitoring] Update Report Settings Failure',
    props<{ error: HttpErrorResponse }>(),
);

export const clearDeviceCareRequestAction = createAction(
    '[Monitoring] Clear Device Care Request',
    props<{ clearDeviceCareRequest: ClearDeviceCareParameters }>(),
);

export const clearDeviceCareSuccessAction = createAction(
    '[Monitoring] Clear Device Care Success',
);

export const clearDeviceCareFailureAction = createAction(
    '[Monitoring] Clear Device Care Failure',
    props<{ error: HttpErrorResponse }>(),
);

export const resetDeviceCareSettingsRequestAction = createAction(
    '[Monitoring] Reset Device Care Settings Request',
);

export const resetDeviceCareSettingsSuccessAction = createAction(
    '[Monitoring] Reset Device Care Settings Success',
);

export const resetDeviceCareSettingsFailureAction = createAction(
    '[Monitoring] Reset Device Care Settings Failure',
    props<{ error: HttpErrorResponse }>(),
);

export const getSiteAlertsRequestAction = createAction(
    '[Monitoring] Get Site Alerts Request',
);

export const getCurrentSiteAlertsRequestAction = createAction(
    '[Monitoring] Get Current Site Alerts Request',
);

export const getSiteAlertsSuccessAction = createAction(
    '[Monitoring] Get Site Alerts Success',
    props<{ alerts: AlertInterval[] }>(),
);

export const getCurrentSiteAlertsSuccessAction = createAction(
    '[Monitoring] Get Current Site Alerts Success',
    props<{ alerts: AlertInterval[] }>(),
);

export const getSiteAlertsFailureAction = createAction(
    '[Monitoring] Get Site Alerts Failure',
    props<{ error: HttpErrorResponse }>(),
);

export const getCurrentSiteAlertsFailureAction = createAction(
    '[Monitoring] Get Current Site Alerts Failure',
    props<{ error: HttpErrorResponse }>(),
);

export const updateSiteReportMetricsAction = createAction(
    '[Monitoring] Update Site Report Metrics',
    props<{ metrics: string[]; siteId: string }>(),
);

export const getSiteReportsRequestAction = createAction(
    '[Monitoring] Get Site Reports Request',
);

export const getSiteReportsSuccessAction = createAction(
    '[Monitoring] Get Site Reports Success',
    props<{ reports: Report[] }>(),
);

export const getSiteReportsFailureAction = createAction(
    '[Monitoring] Get Site Reports Failure',
    props<{ error: HttpErrorResponse }>(),
);

export const getSiteReportRequestAction = createAction(
    '[Monitoring] Get Site Report Request',
    props<{ reportId: string; reportName: string }>(),
);

export const getSiteSmtpStatusRequestAction = createAction(
    '[Monitoring] Get Site SMTP Status Request',
);

export const getSiteSmtpStatusSuccessAction = createAction(
    '[Monitoring] Get Site SMTP Status Success',
    props<{ siteId: string; smtpStatus: SmtpStatus }>(),
);

export const getSiteSmtpStatusFailureAction = createAction(
    '[Monitoring] Get Site SMTP Status Failure',
    props<{ error: HttpErrorResponse }>(),
);
